import { isEmpty, size } from 'lodash';
import { LOWER_CASE, NUMBER, PUNCTUATIONS, SPECIAL_CHARACTERS, UPPER_CASE } from '@core/lib/constants/regexpValidations';

export type ValidationFunctions = [(...args: any[]) => boolean, () => string];

export type ValidationMapper = { [field: string]: ValidationFunctions[] };

export type SizeType = Record<string, unknown> | string | null | undefined;

/**
 * Used when there is no specific error message on input field
 * but we still want the validation to fail
 */
export const NO_MSG = 'no-error-msg';

/**
 * Validate a field based on specified set of rules
 */
export const validate = (validations: ValidationFunctions[]) => (...args: any[]) => {
  let idx = 0;
  while (idx < validations.length) {
    const [runValidation, getMessage] = validations[idx];
    if (runValidation(...args)) {
      return getMessage();
    }
    idx += 1;
  }
};

const checkType = (type: string) => (value?: any) => {
  const valueType = typeof value;
  return valueType === type;
};

const isNull = (value: any): value is null => value === null;

const isBoolean = checkType('boolean');

const isNumber = checkType('number');

/**
 * Checks if the input has some value
 */
export const isRequired = (value?: any) => !isBoolean(value) && !isNumber(value) && isEmpty(value);

/**
 * Regexp for email validation
 */
// eslint-disable-next-line
const rEmail = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

/**
 * Regexp for domain validation
 */
// eslint-disable-next-line
const rDomain = /^([a-z0-9])(([a-z0-9-]{1,61})?[a-z0-9]{1})?(\.[a-z0-9](([a-z0-9-]{1,61})?[a-z0-9]{1})?)?(\.[a-zA-Z]{2,12})+$/i;

/**
 * Regexp for Adjust server validation
 */
const rAdjustServer = /adjust\.com|adjust\.io|adj\.st/i;

/**
 * Regexp for non ASCII printable characters
 */
const rAsciiCharacters = /[^ -~]+/i;

/**
 * Checks if the input value is not lower than minValue
 * NOTE: `value` can be also a `null` or an empty string representing an empty field.
 *       And these values are valid, unless the field is required.
 */
export const min = (minValue: any) => (value: any) => !isNull(value) && value !== '' && Number(value) < minValue;

/**
 * Checks if the input value is not greater than maxValue
 * NOTE: `value` can be also a `null` or an empty string representing an empty field.
 *       And these values are valid, unless the field is required.
 */
export const max = (maxValue: any) => (value: any) => !isNull(value) && value !== '' && Number(value) > maxValue;

/**
 * Checks if the length of input value is lower than the provided number
 */
export const hasMinLength = (length: number) => (value?: SizeType) => size(value) <= length;

/**
 * Checks if the length of input value is greater than the provided number
 */
export const hasMaxLength = (length: number) => (value: SizeType) => size(value) > length;

/**
 * Compares two different input values
 */
export const sameAs = (to: any) => (value: any) => to !== value;

/**
 * Checks if value is not an email
 */
export const isEmail = (value: string) => !rEmail.test(value);

/**
 * Checks if any of the values is not an email
 */
export const areAllEmails = (emails: string[]) => emails.some(isEmail);

/**
 * Checks if value is not valid URL
 */
export const isURL = (value: string) =>
  // eslint-disable-next-line no-useless-escape
  !/^(http|https):\/\/(\w+:?\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@\-\/]))?$/gi.test(value);

export const isNotURL = isURL;

/**
 * Checks if value contains only white-spaces
 */
export const noWhitespace = (value: string) => /^\s+$/g.test(value);

/**
 * Checks if value is not an domain
 */
export const isDomain = (value: string) => !rDomain.test(value);

/**
 * Checks if any of the values is not an domain
 */
export const areAllDomains = (domains: string[]) => domains.some(isDomain);

/**
 * Checks if value contains an URL to Adjust server
 */
export const hasAdjustServer = (value: string) => rAdjustServer.test(value);

/**
 * Checks if value contains non ASCII printable characters
 */
export const onlyAsciiCharacters = (value: string) => rAsciiCharacters.test(value);

/**
 * Checks if the input value matches regular expression
 * NOTE: `value` can be also a `null` or an empty string representing an empty field.
 *       And these values are valid, unless the field is required.
 */
export const matchesRegExp = (regExp: RegExp) => (value: string | null) =>
  !isNull(value) && value !== '' && !regExp.test(value);

  /**
 * Checks if value contains numbers
 */
  export const hasNumber = (value: string): boolean => NUMBER.test(value);

  /**
 * Checks if value contains lowercase characters
 */
  export const hasLowerCase = (value: string): boolean => LOWER_CASE.test(value);

  /**
 * Checks if value contains uppercase characters
 */
  export const hasUpperCase = (value: string): boolean => UPPER_CASE.test(value);

  /**
 * Checks if value contains punctuation
 */
  export const hasPunctuation = (value: string): boolean => PUNCTUATIONS.test(value);

  /**
 * Checks if value contains special characters
 */
  export const hasSpecialCharacters = (value: string): boolean => SPECIAL_CHARACTERS.test(value);

  /**
 * Checks if value meets at least three of validation requirements
 */
  export const isPasswordInvalid = (value: string): boolean => {
    let count = 0;
    const validations = [
      hasNumber(value),
      hasLowerCase(value),
      hasUpperCase(value),
      hasPunctuation(value),
      hasSpecialCharacters(value),
    ];
    validations.forEach(validation => {
      if (validation === true) {
        count++;
      }
    });
    return count < 3;
  };
